import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AdminPageLayout } from "@/layouts/AdminPageLayout";
import { getGroups, createGroup } from "@/services/admin/vocabularyGroups.service";
import { useAppToast } from "@/hooks/useAppToast";
import { Group } from "@/services/admin/vocabularyGroups.interfaces";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BaseToolbar } from "@/components/ui/base-toolbar";

const AdminVocabularyManagementPage: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", description: "" });
  const [isCreating, setIsCreating] = useState(false);
  const toast = useAppToast();

  const loadGroups = async () => {
    setIsLoading(true);
    try {
      const { data } = await getGroups({});
      setGroups(data);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to load vocabulary groups");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []);

  useEffect(() => {
    const filtered = groups.filter((group) =>
      group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      group.description?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredGroups(filtered);
  }, [groups, searchQuery]);

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setIsCreating(true);
    try {
      await toast.promise(
        createGroup({
          name: newGroup.name,
          description: newGroup.description
        }),
        {
          loading: "Creating group...",
          success: "Group created successfully",
          error: "Failed to create group"
        }
      );
      
      setCreateDialogOpen(false);
      setNewGroup({ name: "", description: "" });
      await loadGroups();
    } catch (error) {
      console.error("Error creating group:", error);
    } finally {
      setIsCreating(false);
    }
  };

  if (isLoading) {
    return (
      <AdminPageLayout title="Loading Vocabulary Groups...">
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout title="Vocabulary Management">
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Vocabulary Group</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <Input
                value={newGroup.name}
                onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                placeholder="Enter group name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={newGroup.description}
                onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                placeholder="Enter group description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateDialogOpen(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateGroup}
              disabled={isCreating}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="space-y-6">
        <h2 className="text-lg font-medium">Vocabulary Groups</h2>
        <BaseToolbar
          searchPlaceholder="Search vocabulary groups..."
          buttonLabel="Create New Group"
          onCreateClick={() => setCreateDialogOpen(true)}
          onSearchChange={setSearchQuery}
          searchValue={searchQuery}
        />

        {groups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No vocabulary groups yet. Create one to get started.
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No groups match your search.
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredGroups.map((group) => (
              <Link
                key={group.id}
                to={`/admin/educational/vocabulary-management/${group.id}`}
                className="block border rounded-lg p-4 hover:border-blue-500 transition-colors"
              >
                <h3 className="font-medium mb-2">{group.name}</h3>
                {group.description && (
                  <p className="text-sm text-gray-500 line-clamp-2">{group.description}</p>
                )}
                <div className="mt-2 text-sm text-gray-400">
                  {group.words.length} words
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AdminPageLayout>
  );
};

export default AdminVocabularyManagementPage;